<template>
  <div class="asset-list-page">

    <!-- ===================== FILTER PANEL ===================== -->
    <transition name="slide-down">
      <b-card v-if="showFilters" no-body class="filter-panel mb-2">
        <b-card-header class="filter-panel__header">
          <div class="d-flex align-items-center">
            <div class="filter-panel__icon-wrapper mr-75">
              <feather-icon icon="SlidersIcon" size="16" />
            </div>
            <h6 class="mb-0 font-weight-bolder">{{ $t('Filters') }}</h6>
            <b-badge v-if="hasActiveFilters" pill variant="primary" class="ml-75 filter-active-badge">
              {{ $t('Active') }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="hasActiveFilters"
              variant="flat-danger"
              size="sm"
              class="btn-icon mr-50"
              @click="clearAllFilters"
              v-b-tooltip.hover
              :title="$t('Clear all filters')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon filter-close-btn"
              @click="showFilters = false"
              v-b-tooltip.hover
              :title="titleTootipBtnCloseFilter"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="filter-panel__body pt-75">
          <b-row>
            <!-- Manufacturer -->
            <b-col cols="12" md="6" class="mb-1">
              <label class="filter-label">
                <feather-icon icon="CpuIcon" size="12" class="mr-25" />
                {{ $t('Manufacturer') }}
              </label>
              <v-select
                v-model="assetBrandId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="manufacturerOptions"
                :reduce="val => val.value"
                :clearable="true"
                input-id="manufacturer"
                class="filter-v-select"
              />
            </b-col>

            <!-- Asset Type -->
            <b-col cols="12" md="6" class="mb-1">
              <label class="filter-label">
                <feather-icon icon="TruckIcon" size="12" class="mr-25" />
                {{ $t('Asset Type') }}
              </label>
              <v-select
                v-model="assetTypeId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="assetsOptions"
                :reduce="val => val.value"
                :clearable="true"
                input-id="asset-type"
                class="filter-v-select"
              />
            </b-col>

            <!-- Owner -->
            <b-col cols="12" md="6" class="mb-1">
              <label class="filter-label">
                <feather-icon icon="UserIcon" size="12" class="mr-25" />
                {{ $t('Owner') }}
              </label>
              <vue-autosuggest
                v-model="searchOwner"
                :suggestions="filteredOptions"
                :input-props="inputProps"
                @selected="selectHandler"
                @input="onInputChange"
              >
                <template slot-scope="{ suggestion }">
                  <span class="my-suggestion-item font-weight-bold">{{ suggestion.item.name }}</span>
                  <div v-show="suggestion.item.ownerName">
                    <small class="text-muted">{{ $t('Belongs to: ') }} <strong>{{ suggestion.item.ownerName }}</strong></small>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Status -->
            <b-col cols="12" md="6" class="mb-1">
              <label class="filter-label">
                <feather-icon icon="ActivityIcon" size="12" class="mr-25" />
                {{ $t('Status') }}
              </label>
              <multiselect
                v-model="chosenStatus"
                :options="options"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="placeholderSelect"
                label="description"
                track-by="description"
                :preselect-first="false"
              >
                <template slot="selection" slot-scope="{ values, search, isOpen }">
                  <span class="multiselect__single" v-if="values.length && !isOpen">
                    {{ values.length }}&nbsp;{{ $t('options selected') }}
                  </span>
                </template>
              </multiselect>
            </b-col>

            <!-- Asset Group -->
            <b-col cols="12" md="6" class="mb-1">
              <label class="filter-label">
                <feather-icon icon="GridIcon" size="12" class="mr-25" />
                {{ $t('Asset Group') }}
              </label>
              <vue-autosuggest
                v-model="searchAssetGroup"
                :suggestions="filteredOptionsAssetGroup"
                :input-props="inputPropsAssetGroup"
                @selected="selectHandlerAssetGroup"
                @input="onInputChangeAssetGroup"
              >
                <template slot-scope="{ suggestion }">
                  <span class="my-suggestion-item font-weight-bold">{{ suggestion.item.name }}</span>
                  <div v-show="suggestion.item.assetsCount !== undefined">
                    <small class="text-muted">{{ $t('Amount of assets: ') }} <strong>{{ suggestion.item.assetsCount }}</strong></small>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- ===================== MAIN CARD ===================== -->
    <b-card no-body class="asset-list-card">

      <!-- Toolbar -->
      <div class="asset-list-toolbar">
        <div class="toolbar-left">
          <div class="per-page-control">
            <label class="per-page-label">{{ $t('Show') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector"
            />
            <label class="per-page-label">{{ $t('entries') }}</label>
          </div>
        </div>

        <div class="toolbar-right asset-list-top-actions">
          <!-- Search Input -->
          <div class="search-input-wrapper">
            <feather-icon icon="SearchIcon" size="16" class="search-icon" />
            <b-form-input
              v-model="searchQuery"
              class="search-input"
              :placeholder="$t('Search assets...')"
            />
          </div>

          <!-- Separator -->
          <span class="toolbar-separator d-none d-md-block" />

          <!-- Action Buttons Group -->
          <div class="toolbar-actions">
            <!-- Filter Toggle -->
            <b-button
              v-show="!showFilters"
              class="btn-icon toolbar-btn position-relative"
              :variant="hasActiveFilters ? 'success' : 'outline-secondary'"
              @click="showFilters = true"
              v-b-tooltip.hover
              :title="hasActiveFilters ? $t('Open Filters (Active)') : titleTootipBtnFilter"
            >
              <feather-icon icon="FilterIcon" size="15" />
              <b-badge
                v-if="hasActiveFilters"
                variant="warning"
                pill
                class="position-absolute badge-notification"
              >!</b-badge>
            </b-button>

            <!-- Clear Filters -->
            <b-button
              v-if="hasActiveFilters"
              variant="flat-danger"
              class="toolbar-btn clear-filters-btn"
              @click="clearAllFilters"
              v-b-tooltip.hover
              :title="$t('Clear Filters')"
            >
              <feather-icon icon="XCircleIcon" size="15" class="mr-25" />
              <span class="d-none d-lg-inline">{{ $t('Clear') }}</span>
            </b-button>

            <!-- Column Selector -->
            <b-dropdown
              variant="outline-secondary"
              class="toolbar-btn"
              boundary="viewport"
              menu-class="asset-list-column-menu"
              no-caret
              :popper-opts="{
                strategy: 'fixed',
                modifiers: [{ name: 'flip', enabled: false }],
              }"
              v-b-tooltip.hover.noninteractive
              :title="$t('Select Columns')"
            >
              <template #button-content>
                <feather-icon icon="ColumnsIcon" size="15" />
                <b-badge
                  v-if="selectedColumns.length < availableColumns.length"
                  variant="primary"
                  pill
                  class="ml-25 font-small-1"
                >{{ selectedColumns.length }}</b-badge>
              </template>

              <div class="column-dropdown-content px-1 py-75" @click.stop>
                <div class="d-flex justify-content-between align-items-center mb-75 pb-50 border-bottom">
                  <span class="font-weight-bolder font-small-3">{{ $t('Select Columns') }}</span>
                  <div class="d-flex align-items-center gap-50">
                    <b-button @click="resetColumnsToDefault" size="sm" variant="link" class="p-0 text-secondary font-small-2">
                      <feather-icon icon="RotateCcwIcon" size="11" class="mr-25" />{{ $t('Default') }}
                    </b-button>
                  </div>
                </div>

                <div class="column-options-list my-75" style="max-height:240px; overflow-y:auto; overflow-x:hidden;">
                  <b-form-checkbox-group v-model="selectedColumns" stacked>
                    <div
                      v-for="column in orderedAvailableColumns"
                      :key="column.key"
                      class="column-drag-item d-flex align-items-center mb-50"
                      draggable="true"
                      @dragstart="onColSelectorDragStart($event, column.key)"
                      @dragover="onColSelectorDragOver"
                      @dragleave="onColSelectorDragLeave"
                      @drop="onColSelectorDrop($event, column.key)"
                      @dragend="onColSelectorDragEnd"
                    >
                      <feather-icon icon="MenuIcon" size="14" class="mr-50 cursor-move text-muted" />
                      <b-form-checkbox
                        :value="column.key"
                        :disabled="selectedColumns.length === 1 && selectedColumns.includes(column.key)"
                        class="custom-control-primary custom-column-checkbox m-0"
                      >
                        <span class="font-small-3 font-weight-500">{{ column.label }}</span>
                      </b-form-checkbox>
                    </div>
                  </b-form-checkbox-group>
                </div>

                <div class="pt-50 border-top">
                  <small class="text-muted">{{ selectedColumns.length }} {{ $t('of') }} {{ availableColumns.length }} {{ $t('columns selected') }}</small>
                </div>
              </div>
            </b-dropdown>

            <!-- Options Dropdown -->
            <b-dropdown variant="primary" class="toolbar-btn" boundary="viewport" menu-class="asset-list-top-options-menu" no-caret>
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="15" class="d-sm-none" />
                <span class="d-none d-sm-inline">{{ $t('Options') }}</span>
                <feather-icon icon="ChevronDownIcon" size="13" class="d-none d-sm-inline ml-25" />
              </template>
              <b-dropdown-item
                v-if="user.role == 'TRCKOWN' || user.role == 'SERVSHAR' || user.role == 'DEALER' || user.role == 'SYSADMIN' || user.profile == 'SYSADMIN'"
                :to="{ name: 'register-asset' }"
              >
                <feather-icon icon="PlusCircleIcon" class="mr-50" />
                {{ $t('Add Asset') }}
              </b-dropdown-item>
              <b-dropdown-item @click="exportXls">
                <feather-icon icon="FileTextIcon" class="mr-50" />
                {{ $t('Export Sheet') }}
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="openNewFeaturesModal">
                <feather-icon icon="HelpCircleIcon" class="mr-50 text-primary" />
                {{ $t('Whats New & Tips') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <b-table
        ref="refAssetListTable"
        :items="fetchAssets"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="emptyText"
        :sort-desc.sync="sortDesc"
        class="asset-data-table"
        tbody-tr-class="asset-table-row"
        @row-clicked="onRowClicked"
      >

        <!-- ===================== SKELETON LOADER ===================== -->
        <template #table-busy>
          <div class="asset-table-skeleton-wrapper" aria-hidden="true" role="status" aria-label="Loading assets">
            <table class="table b-table asset-skeleton-table mb-0">
              <tbody>
                <tr
                  v-for="(row, rowIdx) in skeletonRows"
                  :key="`skel-row-${rowIdx}`"
                  class="asset-skeleton-row"
                  :style="{ '--sk-delay': `${rowIdx * 0.06}s` }"
                >
                  <td
                    v-for="col in tableColumns"
                    :key="`skel-${rowIdx}-${col.key}`"
                    class="asset-skeleton-cell"
                    :class="`col-${col.key}`"
                  >
                    <!-- Column: Owner -->
                    <template v-if="col.key === 'ownername'">
                      <div class="cell-owner">
                        <div style="max-width: 160px;">
                          <b-skeleton :width="row.ownerWidth" height="15px" class="rounded-sm mb-50" animation="wave" />
                          <div class="d-flex align-items-center">
                            <b-skeleton type="avatar" size="11px" class="mr-25 rounded-sm flex-shrink-0" animation="wave" />
                            <b-skeleton :width="row.dealerWidth" height="11px" class="rounded-sm" animation="wave" />
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Column: Cost Center -->
                    <template v-else-if="col.key === 'costCenter'">
                      <div class="py-25">
                        <b-skeleton :width="row.costCenterWidth" height="14px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Plate -->
                    <template v-else-if="col.key === 'plate'">
                      <div class="plate-chip plate-chip--skeleton">
                        <b-skeleton type="avatar" size="12px" class="mr-50 rounded-sm flex-shrink-0 plate-sk-icon" animation="wave" />
                        <b-skeleton width="60px" height="13px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Nickname -->
                    <template v-else-if="col.key === 'nickname'">
                      <div class="py-25">
                        <b-skeleton :width="row.nicknameWidth" height="14px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Model -->
                    <template v-else-if="col.key === 'modelName'">
                      <div class="model-cell">
                        <b-skeleton type="avatar" size="40px" class="mr-1 rounded-circle flex-shrink-0 model-sk-avatar" animation="wave" />
                        <div class="flex-grow-1" style="min-width: 80px; max-width: 140px;">
                          <b-skeleton :width="row.modelWidth" height="14px" class="rounded-sm mb-50" animation="wave" />
                          <b-skeleton :width="row.brandWidth" height="11px" class="rounded-sm" animation="wave" />
                        </div>
                      </div>
                    </template>

                    <!-- Column: Chassis -->
                    <template v-else-if="col.key === 'chassis'">
                      <div class="d-flex align-items-center py-25">
                        <b-skeleton type="avatar" size="11px" class="mr-25 rounded-sm flex-shrink-0" animation="wave" />
                        <b-skeleton :width="row.chassisWidth" height="14px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Hourmeter -->
                    <template v-else-if="col.key === 'hourmeter'">
                      <div class="d-flex align-items-center py-25">
                        <b-skeleton :width="row.hourmeterWidth" height="14px" class="rounded-sm mr-25" animation="wave" />
                        <b-skeleton width="11px" height="11px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Odometer -->
                    <template v-else-if="col.key === 'odometer'">
                      <div class="d-flex align-items-center py-25">
                        <b-skeleton :width="row.odometerWidth" height="14px" class="rounded-sm mr-25" animation="wave" />
                        <b-skeleton width="15px" height="11px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Last Position -->
                    <template v-else-if="col.key === 'lastPositionTime'">
                      <div class="connection-row py-25">
                        <b-skeleton width="115px" height="14px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Last Telemetry -->
                    <template v-else-if="col.key === 'lastTelemetryTime'">
                      <div class="connection-row py-25">
                        <b-skeleton width="115px" height="14px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Status -->
                    <template v-else-if="col.key === 'workstatus'">
                      <div class="status-badge status-badge--skeleton">
                        <span class="status-led skeleton-led" :class="`skeleton-led--${row.statusColor}`"></span>
                        <b-skeleton :width="row.statusWidth" height="12px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                    <!-- Column: Actions -->
                    <template v-else-if="col.key === 'actions'">
                      <div class="actions-cell d-flex align-items-center justify-content-center py-25">
                        <b-skeleton type="avatar" size="28px" class="rounded-circle action-sk-btn" animation="wave" />
                      </div>
                    </template>

                    <!-- Generic fallback for any other column -->
                    <template v-else>
                      <div class="py-25">
                        <b-skeleton width="80%" height="14px" class="rounded-sm" animation="wave" />
                      </div>
                    </template>

                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- Column: Owner -->
        <template #head(ownername)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Owner') }}
          </div>
        </template>
        <template #cell(ownername)="data">
          <div class="cell-owner">
            <div class="d-flex align-items-center">
              <div style="max-width: 160px; overflow: hidden;">
                <BasicMarquee class="font-weight-bold" v-b-tooltip.hover :title="data.item.customerName">
                  {{ data.item.customerName }}
                </BasicMarquee>
                <BasicMarquee v-if="data.item.dealerName" class="text-muted d-block mt-25" v-b-tooltip.hover :title="data.item.dealerName">
                  <small>
                    <feather-icon icon="BriefcaseIcon" size="10" class="mr-25" />{{ data.item.dealerName }}
                  </small>
                </BasicMarquee>
              </div>
            </div>
          </div>
        </template>

        <!-- Column: Cost Center -->
        <template #head(costCenter)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Cost Center') }}
          </div>
        </template>
        <template #cell(costCenter)="data">
          <span class="text-body-2">{{ data.item.costCenter || '—' }}</span>
        </template>

        <!-- Column: Plate -->
        <template #head(plate)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Plate') }}
          </div>
        </template>
        <template #cell(plate)="data">
          <b-link
            v-if="data.item.plate"
            :to="{ name: 'apps-asset-summary', params: { id: data.item.id } }"
            class="plate-chip"
          >
            <feather-icon icon="CreditCardIcon" size="11" class="mr-25" />
            <span class="text-uppercase font-weight-bolder">{{ data.item.plate }}</span>
          </b-link>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Column: Nickname -->
        <template #head(nickname)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Nickname') }}
          </div>
        </template>
        <template #cell(nickname)="data">
          <b-link
            v-if="data.item.nickname || data.item.nickName"
            :to="{ name: 'apps-asset-summary', params: { id: data.item.id } }"
            class="font-weight-bold text-primary nickname-link"
          >
            {{ data.item.nickname || data.item.nickName }}
          </b-link>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Column: Hourmeter -->
        <template #head(hourmeter)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Hourmeter') }}
          </div>
        </template>
        <template #cell(hourmeter)="data">
          <span v-if="hasMetricValue(data.item.hourmeter !== undefined ? data.item.hourmeter : data.item.Hourmeter)" class="font-weight-bold text-nowrap">
            {{ formatMetricNumber(data.item.hourmeter !== undefined ? data.item.hourmeter : data.item.Hourmeter) }} <small class="text-muted">h</small>
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Column: Odometer -->
        <template #head(odometer)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Odometer') }}
          </div>
        </template>
        <template #cell(odometer)="data">
          <span v-if="hasMetricValue(data.item.odometer !== undefined ? data.item.odometer : data.item.Odometer)" class="font-weight-bold text-nowrap">
            {{ formatMetricNumber(data.item.odometer !== undefined ? data.item.odometer : data.item.Odometer) }} <small class="text-muted">km</small>
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Column: Last Position -->
        <template #head(lastPositionTime)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            <span>{{ $t('Last Position') }}</span>
          </div>
        </template>
        <template #cell(lastPositionTime)="data">
          <div class="connection-row" v-b-tooltip.hover :title="formatAssetDateTime(getPositionTime(data.item)) || $t('Not Reported')">
            <span class="connection-date font-weight-bold" :class="{ 'text-muted': !getPositionTime(data.item) }">
              {{ formatAssetDateTime(getPositionTime(data.item)) || '—' }}
            </span>
          </div>
        </template>

        <!-- Column: Last Telemetry -->
        <template #head(lastTelemetryTime)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            <span>{{ $t('Last Telemetry') }}</span>
          </div>
        </template>
        <template #cell(lastTelemetryTime)="data">
          <div class="connection-row" v-b-tooltip.hover :title="formatAssetDateTime(getTelemetryTime(data.item)) || $t('Not Reported')">
            <span class="connection-date font-weight-bold" :class="{ 'text-muted': !getTelemetryTime(data.item) }">
              {{ formatAssetDateTime(getTelemetryTime(data.item)) || '—' }}
            </span>
          </div>
        </template>

        <!-- Column: WorkStatus -->
        <template #head(workstatus)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center justify-content-center">
            <span>{{ $t('Status') }}</span>
          </div>
        </template>
        <template #cell(workstatus)="data">
          <div class="status-cell d-flex flex-wrap" style="gap:4px;">
            <span class="status-badge" :class="`status-badge--${setColor(data.item.lastWorkstatus)}`">
              <span class="status-led"></span>
              {{ resolveTextStatus(data.item.lastWorkstatus) }}
            </span>
            <span v-show="data.item.pan" class="status-badge status-badge--danger status-badge--pulse">
              <span class="status-led"></span>{{ $t('Panic') }}
            </span>
            <span v-show="data.item.lastTelemetry && data.item.lastTelemetry.ou1 === 1" class="status-badge status-badge--danger status-badge--pulse">
              <span class="status-led"></span>{{ $t('Bloqueado') }}
            </span>
          </div>
        </template>

        <!-- Column: Model -->
        <template #head(modelName)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Model') }}
          </div>
        </template>
        <template #cell(modelName)="data">
          <div class="model-cell">
            <b-avatar
              class="model-avatar mr-1"
              :style="resolveColorNoConnection(data.item.lastWorkstatus)"
              size="40"
              :src="resolveIcon(data.item.assetType, data.item.iconUrl)"
              :text="avatarText(data.item.model)"
              :variant="`${resolveModelAvatarVariant(data.item.lastWorkstatus)}`"
            />
            <div>
              <div class="font-weight-bold">{{ data.item.model || '—' }}</div>
              <div class="text-muted font-small-2">{{ data.item.manufacturerName || '—' }}</div>
            </div>
          </div>
        </template>

        <!-- Column: Chassis -->
        <template #head(chassis)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Chassis') }}
          </div>
        </template>
        <template #cell(chassis)="data">
          <b-link
            v-if="data.item.chassisNumber || data.item.chassis"
            :to="{ name: 'apps-asset-summary', params: { id: data.item.id } }"
            class="chassis-link font-weight-bold"
          >
            <feather-icon icon="HashIcon" size="10" class="mr-25" />
            <span class="text-uppercase">{{ data.item.chassisNumber || data.item.chassis }}</span>
          </b-link>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Column: Actions -->
        <template #head(actions)>
          <div v-b-tooltip.hover :title="$t('Drag to reorder')" class="d-inline-flex align-items-center">
            {{ $t('Actions') }}
          </div>
        </template>
        <template #cell(actions)="data">
          <div class="actions-cell d-flex align-items-center">

            <b-dropdown
              variant="link"
              toggle-class="action-menu-btn"
              no-caret
              :right="$store.state.appConfig.isRTL"
              boundary="viewport"
              :popper-opts="{
                strategy: 'fixed',
                placement: $store.state.appConfig.isRTL ? 'bottom-start' : 'bottom-end',
                modifiers: [{ name: 'flip', enabled: false }],
              }"
              @show="onDropdownShow"
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="15" class="text-body" />
              </template>

              <b-dropdown-item :to="{ name: 'apps-asset-summary', params: { id: data.item.id } }">
                <feather-icon icon="ArrowRightCircleIcon" class="mr-50" />{{ $t('Open') }}
              </b-dropdown-item>

              <b-dropdown-item :to="{ name: 'register-asset', params: { id: data.item.id } }">
                <feather-icon icon="EditIcon" class="mr-50" />{{ $t('Edit') }}
              </b-dropdown-item>

              <b-dropdown-divider v-if="user.role == 'TRCKOWN' || user.role == 'SERVSHAR' || user.role == 'DEALER' || user.role === 'SYSADMIN' || user.profile === 'SYSADMIN'" />

              <b-dropdown-item
                v-if="user.role == 'TRCKOWN' || user.role == 'SERVSHAR' || user.role == 'DEALER'"
                @click="modalDelete(data.item.id)"
                class="text-danger"
              >
                <feather-icon icon="Trash2Icon" class="mr-50 text-danger" />{{ $t('Delete') }}
              </b-dropdown-item>

              <template v-if="user.role === 'SYSADMIN' || user.profile === 'SYSADMIN'">
                <b-dropdown-item @click="showChangeLogModal(data.item.id)">
                  <feather-icon icon="FileTextIcon" class="mr-50" />{{ $t('Ver Change Log') }}
                </b-dropdown-item>
              </template>

              <template v-if="user.role === 'TRCKOWN'">
                <b-dropdown-item v-if="data.item.group" @click="showAssetGroupToggleModal(data.item.id, data.item.group)">
                  <feather-icon icon="MinusSquareIcon" class="mr-50" />{{ $t('Remove from group') }}
                </b-dropdown-item>
                <b-dropdown-item v-else @click="showAssetGroupToggleModal(data.item.id)">
                  <feather-icon icon="PlusSquareIcon" class="mr-50" />{{ $t('Add to a group') }}
                </b-dropdown-item>
              </template>

              <template v-if="user.role === 'TRCKOWN'">
                <b-dropdown-item :disabled="cloningAssetId === data.item.id" @click="cloneAsset(data.item.id)">
                  <b-spinner v-if="cloningAssetId === data.item.id" small class="mr-50" />
                  <feather-icon v-else icon="CopyIcon" class="mr-50" />{{ $t('Clone Asset') }}
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </div>
        </template>

      </b-table>

      <!-- Table Footer / Pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              {{ $t('Showing') }}
              <strong class="text-body">{{ dataMeta.from }}</strong>
              {{ $t('to') }}
              <strong class="text-body">{{ dataMeta.to }}</strong>
              {{ $t('of') }}
              <strong class="text-primary">{{ dataMeta.of }}</strong>
              {{ $t('entries') }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalAssets"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
              <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

    </b-card>

    <!-- Modal: Novas Funcionalidades (Onboarding / PNL) -->
    <b-modal
      v-model="showNewFeaturesModal"
      size="lg"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      modal-class="new-features-modal"
      content-class="new-features-modal-content"
    >
      <div class="new-features-container p-1 p-md-2 position-relative">
        <!-- Botao Fechar -->
        <button
          type="button"
          class="close new-features-close-btn"
          aria-label="Close"
          @click="closeNewFeaturesModal"
        >
          <feather-icon icon="XIcon" size="18" />
        </button>

        <!-- Header -->
        <div class="text-center mb-2">
          <div class="new-features-pill mb-75">
            <feather-icon icon="ZapIcon" size="13" class="mr-50" />
            <span>{{ $t('New Features & Improvements') }}</span>
          </div>
          <h3 class="font-weight-bolder new-features-title mb-50">
            {{ $t('Asset List Tour Title') }}
          </h3>
          <p class="text-muted font-small-3 mx-auto mb-0" style="max-width: 560px;">
            {{ $t('Asset List Tour Subtitle') }}
          </p>
        </div>

        <!-- 3 Feature Cards Grid -->
        <div class="new-features-grid mb-2">
          <!-- Card 1: Horimetro e Odometro -->
          <div class="feature-card">
            <div class="feature-icon-wrapper feature-icon-wrapper--primary mb-75">
              <feather-icon icon="ClockIcon" size="20" />
            </div>
            <h5 class="font-weight-bolder feature-card-title mb-50">
              {{ $t('Asset List Feature Metrics Title') }}
            </h5>
            <p class="feature-card-text text-muted font-small-2 mb-1">
              {{ $t('Asset List Feature Metrics Text') }}
            </p>
            <div class="feature-preview-chips">
              <span class="preview-chip preview-chip--metric mr-50">
                1.420,5 <small class="text-muted font-weight-bold">h</small>
              </span>
              <span class="preview-chip preview-chip--metric">
                48.210 <small class="text-muted font-weight-bold">km</small>
              </span>
            </div>
          </div>



          <!-- Card 3: Arraste & Personalize -->
          <div class="feature-card">
            <div class="feature-icon-wrapper feature-icon-wrapper--success mb-75">
              <feather-icon icon="MoveIcon" size="20" />
            </div>
            <h5 class="font-weight-bolder feature-card-title mb-50">
              {{ $t('Asset List Feature Reorder Title') }}
            </h5>
            <p class="feature-card-text text-muted font-small-2 mb-1">
              {{ $t('Asset List Feature Reorder Text') }}
            </p>
            <div class="feature-preview-chips">
              <span class="preview-chip preview-chip--action">
                <feather-icon icon="ColumnsIcon" size="11" class="mr-25" />
                {{ $t('Columns') }} &bull; {{ $t('Drag to reorder') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Pro Tip Banner (PNL Framing) -->
        <div class="pro-tip-banner mb-2 d-flex align-items-center">
          <div class="pro-tip-icon mr-1">
            <feather-icon icon="AwardIcon" size="20" class="text-warning" />
          </div>
          <div class="font-small-2 pro-tip-text">
            <strong>{{ $t('Pro Tip:') }}</strong> {{ $t('Asset List Tour Pro Tip') }}
          </div>
        </div>

        <!-- Footer / Actions -->
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-center pt-1 border-top">
          <b-form-checkbox v-model="dontShowAgainNewFeatures" class="custom-control-primary mb-1 mb-sm-0 font-small-2 text-muted">
            {{ $t('Dont show this welcome tour again') }}
          </b-form-checkbox>
          <b-button
            variant="primary"
            class="btn-cta-welcome font-weight-bolder d-inline-flex align-items-center"
            @click="closeNewFeaturesModal"
          >
            <span>{{ $t('Explore New Features') }}</span>
            <feather-icon icon="ArrowRightIcon" size="15" class="ml-50" />
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Export Loading -->
    <b-modal ref="modal-export" size="sm" centered hide-header hide-footer no-close-on-esc no-close-on-backdrop hide-header-close>
      <div class="export-modal-body">
        <div class="export-spinner-wrapper">
          <b-spinner variant="primary" style="width:3rem;height:3rem;" />
        </div>
        <h5 class="mt-1 mb-25 font-weight-bolder">{{ $t('Preparing report') }}</h5>
        <p class="text-muted font-small-3 mb-0">{{ $t('This may take a few seconds...') }}</p>
      </div>
    </b-modal>

    <!-- Modal: Change Log -->
    <b-modal ref="modal-changelog" size="xl" centered hide-footer :title="$t('Change Log')" modal-class="changelog-modal">
      <div class="changelog-filters mb-2">
        <b-row>
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <label class="filter-label">
              <feather-icon icon="UserIcon" size="12" class="mr-25" />{{ $t('User') }}
            </label>
            <vue-autosuggest
              v-model="changeLogSearchUser"
              :suggestions="changeLogFilteredUsers"
              :input-props="changeLogInputPropsUser"
              @selected="changeLogSelectUserHandler"
              @input="changeLogOnInputUserChange"
            >
              <template slot-scope="{ suggestion }">
                <span class="my-suggestion-item font-weight-bold">{{ suggestion.item.name }}</span>
                <div v-show="suggestion.item.email">
                  <small class="text-muted">{{ suggestion.item.email }}</small>
                </div>
              </template>
            </vue-autosuggest>
          </b-col>
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <label class="filter-label">
              <feather-icon icon="CalendarIcon" size="12" class="mr-25" />{{ $t('Date Range') }}
            </label>
            <flat-pickr
              v-model="changeLogDateRange"
              class="form-control"
              :config="changeLogDateConfig"
              :placeholder="$t('Select date range')"
            />
          </b-col>
          <b-col cols="12" md="4" class="d-flex align-items-end mb-1 mb-md-0">
            <b-button variant="primary" class="mr-75" @click="fetchChangeLogData(1)">
              <feather-icon icon="SearchIcon" size="14" class="mr-25" />{{ $t('Search') }}
            </b-button>
            <b-button variant="outline-secondary" @click="clearChangeLogFilter(false)">
              <feather-icon icon="XIcon" size="14" class="mr-25" />{{ $t('Clear') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="changeLogItems"
        :fields="changeLogFields"
        responsive
        show-empty
        :empty-text="$t('No matching records found')"
        :busy="isChangeLogBusy"
        class="asset-data-table mt-1"
      >
        <template #table-busy>
          <div class="text-center text-primary my-3">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('Loading') }}...</strong>
          </div>
        </template>
        <template #cell(updatedAt)="data">{{ data.value }}</template>
        <template #cell(modifiedFields)="data">
          <div class="d-flex flex-wrap" style="gap:4px;">
            <span
              v-for="(field, index) in (data.value || '').split(';').filter(Boolean)"
              :key="index"
              class="field-chip"
            >{{ field.trim() }}</span>
          </div>
        </template>
        <template #cell(userName)="data">
          <b-link :to="{ name: 'register-user', params: { id: data.item.userId } }" class="font-weight-bold text-primary">
            {{ data.value }}
          </b-link>
        </template>
        <template #cell(plate)="data">
          <b-link :to="{ name: 'apps-asset-summary', params: { id: data.item.assetId } }" class="plate-chip">
            <span class="text-uppercase font-weight-bolder">{{ data.value }}</span>
          </b-link>
        </template>
        <template #cell(chassisNumber)="data">
          <b-link :to="{ name: 'apps-asset-summary', params: { id: data.item.assetId } }" class="font-weight-bold text-primary">
            {{ data.value }}
          </b-link>
        </template>
      </b-table>

      <div class="asset-list-footer mt-1">
        <div class="footer-meta">
          <span class="text-muted">
            {{ $t('Showing') }} <strong class="text-body">{{ changeLogMeta.from }}</strong>
            {{ $t('to') }} <strong class="text-body">{{ changeLogMeta.to }}</strong>
            {{ $t('of') }} <strong class="text-primary">{{ changeLogMeta.of }}</strong>
            {{ $t('entries') }}
          </span>
        </div>
        <b-pagination
          v-model="changeLogCurrentPage"
          :total-rows="changeLogTotalRows"
          :per-page="10"
          first-number
          last-number
          class="mb-0 pagination-modern"
          prev-class="prev-item"
          next-class="next-item"
          @change="fetchChangeLogData"
        >
          <template #prev-text><feather-icon icon="ChevronLeftIcon" size="17" /></template>
          <template #next-text><feather-icon icon="ChevronRightIcon" size="17" /></template>
        </b-pagination>
      </div>
    </b-modal>

    <!-- Modal: Asset Group -->
    <ContentModal :title="$t('Choose a group to add asset')" target="asset-groups-modal">
      <GroupList :selected-asset-id="assetToAddToGroup" @bind="onAssetAddedToGroup" />
    </ContentModal>

  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink, BBadge,
  BDropdown, BDropdownItem, BDropdownDivider, BPagination, BTooltip, BCardBody,
  BCardHeader, BSpinner, BFormGroup, BFormCheckbox, BFormCheckboxGroup, BSkeleton
} from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { onUnmounted, onMounted, onBeforeUnmount, ref, computed, nextTick, watch } from '@vue/composition-api'

/** Lightweight debounce — evita dependência extra */
const debounce = (fn, delay = 400) => {
  let timer = null
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}
import store from '@/store'
import useAssetList from './useAssetList'
import assetStoreModule from '../assetStoreModule'
import { VueAutosuggest } from 'vue-autosuggest'
import Multiselect from 'vue-multiselect'
import managementStoreModule from "@/views/main/management/managementStoreModule"
import userStoreModule from "@/views/main/user/userStoreModule"
import ContentModal from "@/views/components/modal/ContentModal.vue"
import GroupList from "@/views/main/management/group/GroupList.vue"
import BasicMarquee from "@/views/components/BasicMarquee.vue"
import { ValidationProvider } from "vee-validate"
import router from "@/router"
import { getIntlLocale } from '@/libs/i18n/dateLocale'

export default {
  components: {
    BFormGroup, ValidationProvider, BFormCheckbox, BFormCheckboxGroup, ContentModal,
    BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink, BBadge,
    BDropdown, BDropdownItem, BDropdownDivider, BPagination, BTooltip, BCardBody, BCardHeader,
    vSelect, VueAutosuggest, Multiselect, flatPickr, BSpinner, GroupList, BasicMarquee,
    BSkeleton
  },
  setup(props, context) {
    const { root, refs } = context

    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'
    const USER_APP_STORE_MODULE_NAME = 'app-user'

    const manufacturerOptions = ref([''])
    const assetsOptions = ref([''])

    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)

    onUnmounted(() => {
      if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME)
    })

    const user = localStorage.userData !== undefined ? JSON.parse(localStorage.userData) : {}

    const statusOptions = [
      'on',
      'off',
      'noconnection',
      'severe',
      'iddle',
      'failure'
    ]

    const {
      fetchAssets, tableColumns, allColumns, availableColumns, orderedAvailableColumns,
      selectedColumns, columnOrder, selectAllColumns, deselectAllColumns, resetColumnsToDefault,
      moveColumn, moveColumnByKey, perPage,
      currentPage, totalAssets, dataMeta, perPageOptions, searchQuery, sortBy,
      isSortDirDesc, sortDesc, refAssetListTable, statusFilter, customerId,
      chosenStatus, showFilters, searchOwner, assetBrandId, assetTypeId,
      assetGroupId, refetchData, resolveModelAvatarVariant, resolveAssetStatusVariantAndIcon,
      resolveIcon, clearFilters, updateSearchOwner, updateCustomerId, updateAssetGroupId
    } = useAssetList()

    // Internal Component State
    const assetToAddToGroup = ref('')
    const emptyText = computed(() => root.$t('No matching records found'))
    const titleTootipBtnFilter = computed(() => root.$t('Open Filters'))
    const titleTootipBtnCloseFilter = computed(() => root.$t('Close Filters'))
    const titleFuellingBtn = computed(() => root.$t('Export Excel'))
    const cloningAssetId = ref(null)

    // ====================================================
    // SKELETON LOADER STATE — Variações visuais realistas
    // ====================================================
    const skeletonPresets = [
      { ownerWidth: '88%', dealerWidth: '65%', costCenterWidth: '70px', nicknameWidth: '95px', modelWidth: '85%', brandWidth: '55%', chassisWidth: '120px', hourmeterWidth: '65px', odometerWidth: '75px', statusWidth: '65px', statusColor: 'success' },
      { ownerWidth: '72%', dealerWidth: '45%', costCenterWidth: '60px', nicknameWidth: '80px', modelWidth: '70%', brandWidth: '45%', chassisWidth: '110px', hourmeterWidth: '50px', odometerWidth: '68px', statusWidth: '75px', statusColor: 'warning' },
      { ownerWidth: '92%', dealerWidth: '58%', costCenterWidth: '80px', nicknameWidth: '105px', modelWidth: '90%', brandWidth: '60%', chassisWidth: '130px', hourmeterWidth: '72px', odometerWidth: '85px', statusWidth: '60px', statusColor: 'success' },
      { ownerWidth: '68%', dealerWidth: '50%', costCenterWidth: '65px', nicknameWidth: '88px', modelWidth: '75%', brandWidth: '50%', chassisWidth: '115px', hourmeterWidth: '58px', odometerWidth: '62px', statusWidth: '70px', statusColor: 'danger' },
      { ownerWidth: '82%', dealerWidth: '62%', costCenterWidth: '75px', nicknameWidth: '92px', modelWidth: '80%', brandWidth: '40%', chassisWidth: '125px', hourmeterWidth: '60px', odometerWidth: '80px', statusWidth: '68px', statusColor: 'info' },
      { ownerWidth: '78%', dealerWidth: '40%', costCenterWidth: '55px', nicknameWidth: '76px', modelWidth: '65%', brandWidth: '52%', chassisWidth: '105px', hourmeterWidth: '68px', odometerWidth: '70px', statusWidth: '62px', statusColor: 'secondary' },
      { ownerWidth: '85%', dealerWidth: '55%', costCenterWidth: '70px', nicknameWidth: '100px', modelWidth: '85%', brandWidth: '58%', chassisWidth: '120px', hourmeterWidth: '54px', odometerWidth: '76px', statusWidth: '74px', statusColor: 'success' },
      { ownerWidth: '75%', dealerWidth: '48%', costCenterWidth: '62px', nicknameWidth: '84px', modelWidth: '72%', brandWidth: '46%', chassisWidth: '112px', hourmeterWidth: '62px', odometerWidth: '82px', statusWidth: '65px', statusColor: 'warning' },
      { ownerWidth: '90%', dealerWidth: '64%', costCenterWidth: '78px', nicknameWidth: '96px', modelWidth: '88%', brandWidth: '62%', chassisWidth: '128px', hourmeterWidth: '70px', odometerWidth: '88px', statusWidth: '72px', statusColor: 'success' },
      { ownerWidth: '70%', dealerWidth: '42%', costCenterWidth: '58px', nicknameWidth: '78px', modelWidth: '68%', brandWidth: '42%', chassisWidth: '108px', hourmeterWidth: '56px', odometerWidth: '65px', statusWidth: '60px', statusColor: 'info' },
    ]

    const skeletonRows = computed(() => {
      const count = Number(perPage.value) || 10
      const total = Math.min(Math.max(count, 5), 10)
      return Array.from({ length: total }, (_, i) => skeletonPresets[i % skeletonPresets.length])
    })

    const datasuggest = ref([])
    const filteredOptions = ref([])
    const inputProps = computed(() => ({
      id: 'autosuggest__input',
      class: 'form-control',
      placeholder: root.$t('Search owner ...'),
    }))
    const limit = ref(3)
    const selected = ref(null)
    const options = ref([])

    const searchAssetGroup = ref('')
    const filteredOptionsAssetGroup = ref([])
    const inputPropsAssetGroup = computed(() => ({
      id: 'autosuggest__input__asset_group',
      class: 'form-control',
      placeholder: root.$t('Search asset group ...'),
    }))

    const changeLogAssetId = ref(null)
    const changeLogSearchUser = ref('')
    const changeLogSelectedUserId = ref(null)
    const changeLogFilteredUsers = ref([])
    const changeLogInputPropsUser = computed(() => ({
      id: 'autosuggest__input__changelog_user',
      class: 'form-control',
      placeholder: root.$t('Search user ...'),
    }))
    const changeLogItems = ref([])
    const changeLogFields = computed(() => [
      { key: 'updatedAt', label: root.$t('Date'), sortable: true },
      { key: 'modifiedFields', label: root.$t('Modified Fields'), sortable: false },
      { key: 'userName', label: root.$t('User'), sortable: true },
      { key: 'plate', label: root.$t('Plate'), sortable: true },
      { key: 'chassisNumber', label: root.$t('Chassis Number'), sortable: true },
    ])
    const changeLogDateRange = ref(null)
    const changeLogDateConfig = ref({
      mode: 'range',
      dateFormat: 'Y-m-d',
      altFormat: 'd/m/Y',
      altInput: true,
    })
    const isChangeLogBusy = ref(false)
    const changeLogCurrentPage = ref(1)
    const changeLogTotalRows = ref(0)
    const changeLogMeta = ref({ from: 0, to: 0, of: 0 })

    const hasActiveFilters = computed(() => {
      return searchQuery.value ||
        searchOwner.value ||
        customerId.value ||
        assetTypeId.value ||
        assetBrandId.value ||
        assetGroupId.value ||
        (chosenStatus.value && chosenStatus.value.length > 0)
    })

    const placeholderSelect = computed(() => root.$t('Select status ...'))

    // Methods
    const showChangeLogModal = (assetId) => {
      changeLogAssetId.value = assetId
      clearChangeLogFilter(true)
      refs['modal-changelog'].show()
    }

    const clearChangeLogFilter = (fetchInitial = true) => {
      changeLogSearchUser.value = ''
      changeLogSelectedUserId.value = null
      changeLogFilteredUsers.value = []
      changeLogDateRange.value = null
      changeLogCurrentPage.value = 1
      if (fetchInitial) {
        fetchChangeLogData(1)
      } else {
        changeLogItems.value = []
        changeLogTotalRows.value = 0
        changeLogMeta.value = { from: 0, to: 0, of: 0 }
      }
    }

    const fetchChangeLogData = (page = 1) => {
      isChangeLogBusy.value = true
      changeLogCurrentPage.value = page

      let startDate = null
      let endDate = null
      if (changeLogDateRange.value) {
        const dates = changeLogDateRange.value.split(' to ')
        if (dates.length === 2) {
          startDate = dates[0]
          endDate = dates[1]
        } else if (dates.length === 1) {
          startDate = dates[0]
          endDate = dates[0]
        }
      }

      store.dispatch('app-asset/fetchAssetsChangeLog', {
        assetId: changeLogAssetId.value,
        userId: changeLogSelectedUserId.value,
        startDate: startDate,
        endDate: endDate,
        page: changeLogCurrentPage.value,
        perPage: 10
      })
      .then(response => {
        changeLogItems.value = response.data.data || []
        changeLogTotalRows.value = response.data.totalItems || 0

        let from = (changeLogCurrentPage.value - 1) * 10 + 1
        let to = changeLogCurrentPage.value * 10
        if (to > changeLogTotalRows.value) to = changeLogTotalRows.value
        if (changeLogTotalRows.value === 0) from = 0

        changeLogMeta.value = { from, to, of: changeLogTotalRows.value }
      })
      .catch(() => {})
      .finally(() => {
        isChangeLogBusy.value = false
      })
    }

    const changeLogSelectUserHandler = (option) => {
      changeLogFilteredUsers.value = []
      changeLogSearchUser.value = option.item.name
      changeLogSelectedUserId.value = option.item.id
    }

    // Debounced API call — evita requisições a cada tecla digitada no changelog
    const _fetchChangeLogUsers = debounce((text) => {
      store.dispatch('app-user/fetchUsers', { q: text })
      .then(response => {
        changeLogFilteredUsers.value = [{ data: response.data.data }]
      })
      .catch(() => {})
    }, 400)

    const changeLogOnInputUserChange = (text) => {
      changeLogSearchUser.value = text || ''
      if (!text || text.length < 3) {
        changeLogSelectedUserId.value = null
        changeLogFilteredUsers.value = []
        return
      }

      _fetchChangeLogUsers(text)
    }

    const cloneAsset = async (id) => {
      if (cloningAssetId.value) return

      cloningAssetId.value = id
      try {
        const response = await store.dispatch('app-asset/cloneAsset', id)
        const apiMessage = response?.data?.message || 'Asset cloned successfully.'

        root.$swal(root.$t('Success'), root.$t(apiMessage), 'success')
        refetchData()
      } catch (error) {
        const apiMessage = error?.response?.data?.message || 'Asset could not be cloned.'
        root.$swal(root.$t('Error'), root.$t(apiMessage), 'error')
      } finally {
        cloningAssetId.value = null
      }
    }

    const selectHandlerAssetGroup = (option) => {
      filteredOptionsAssetGroup.value = []
      searchAssetGroup.value = option.item.name
      updateAssetGroupId(option.item.id)
    }

    // Debounced API call — evita requisições a cada tecla digitada
    const _fetchGroupSuggestions = debounce((text) => {
      store.dispatch('app-management/fetchAllGroup', {
        q: text,
        page: 1,
        perPage: 10
      })
      .then(response => {
        filteredOptionsAssetGroup.value = [{ data: response.data.data }]
      })
      .catch(() => {})
    }, 400)

    const onInputChangeAssetGroup = (text) => {
      searchAssetGroup.value = text || ''

      if (text === '' || text === undefined || text.length < 3) {
        updateAssetGroupId(null)
        filteredOptionsAssetGroup.value = []
        return
      }

      _fetchGroupSuggestions(text)
    }

    const onAssetAddedToGroup = () => {
      root.$bvModal.hide('asset-groups-modal')
      refetchData()
    }

    const showModalExport = () => {
      refs['modal-export'].show()
    }

    const hideModalExport = () => {
      refs['modal-export'].hide()
    }

    const exportXls = async () => {
      showModalExport()
      await store.dispatch('app-asset/exportAssetsExcel', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: sortDesc.value,
        status: statusFilter.value,
        customerId: customerId.value,
        assetBrandId: assetBrandId.value,
        assetTypeId: assetTypeId.value,
        assetGroupId: assetGroupId.value,
        workStatusList: chosenStatus.value
      })
      .then((response) => {
        var date = new Date().toLocaleDateString()
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `assets_${date}.xls`)
        document.body.appendChild(link)
        link.click()
        hideModalExport()
      })
    }

    const exportPDF = async () => {
      showModalExport()
      await store.dispatch('app-asset/exportAssetsPDF', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: sortDesc.value,
        status: statusFilter.value,
        customerId: customerId.value,
        assetBrandId: assetBrandId.value,
        assetTypeId: assetTypeId.value,
        assetGroupId: assetGroupId.value,
        workStatusList: chosenStatus.value
      })
      .then((response) => {
        var date = new Date().toLocaleDateString()
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `assets_${date}.pdf`)
        document.body.appendChild(link)
        link.click()
        hideModalExport()
      })
    }

    const resolveTextStatus = (status) => {
      if (status === 'neverconnected' || status === '') return root.$t('Never Connected')
      return root.$t(status)
    }

    const fetchTypesWorkStatus = () => {
      store.dispatch(`app-asset/fetchTypesWorkStatus`)
      .then(response => {
        options.value = response.data.data
      })
      .catch(() => {})
    }

    const selectHandler = (option) => {
      selected.value = option.item
      filteredOptions.value = []
      updateSearchOwner(option.item.name)
      updateCustomerId(option.item.id)
    }

    const onDropdownShow = (bvEvent) => {
      nextTick(() => {
        const toggle = bvEvent.target
        const rect = toggle.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom - 8
        const menu = toggle.closest('.btn-group').querySelector('.dropdown-menu')

        if (menu) {
          menu.style.maxHeight = `${Math.max(spaceBelow, 120)}px`
          menu.style.overflowY = 'auto'
        }
      })
    }

    // Debounced API call — evita requisições a cada tecla digitada
    const _fetchOwnerSuggestions = debounce((text) => {
      store.dispatch('app-asset/fetchCustomers', { q: text })
      .then(response => {
        filteredOptions.value = [{ data: response.data.data }]
      })
      .catch(() => {})
    }, 400)

    const onInputChange = (text) => {
      updateSearchOwner(text || '')

      if (text === '' || text === undefined || text.length < 3) {
        updateCustomerId('')
        filteredOptions.value = []
        return
      }

      _fetchOwnerSuggestions(text)
    }

    const clearAllFilters = () => {
      clearFilters()
      filteredOptions.value = []
      selected.value = null
      searchAssetGroup.value = ''
      filteredOptionsAssetGroup.value = []
      refetchData()
    }

    const resolveClass = (status) => {
      if (status === 'noconnection' || status === '' || status === 'neverconnected') return 'noconnection'
      return ''
    }

    const setColor = (status) => {
      if (status === 'failure') return 'danger'
      if (status === 'off') return 'secondary'
      if (status === 'noconnection') return 'light'
      if (status === 'severe') return 'warning'
      if (status === 'iddle') return 'info'
      if (status === 'on') return 'success'
      return 'light'
    }

    const resolveColorNoConnection = (status) => {
      if (status === 'noconnection' || status === '' || status === 'neverconnected') return { 'background-color': '#f8f9fa' }
      return ''
    }

    const realtimeUpdateBlock = (block) => {
      if (!block || block.ou1 == null || !block.assetId) return

      if (refAssetListTable.value && refAssetListTable.value.localItems) {
        refAssetListTable.value.localItems.forEach(item => {
          if (item && item.id === block.assetId) {
            if (!item.lastTelemetry) {
              root.$set(item, 'lastTelemetry', {})
            }
            root.$set(item.lastTelemetry, 'ou1', block.ou1)
          }
        })
      }
    }

    const realtimeUpdatePan = (panic) => {
      if (panic == null || panic.assetId == null || panic.pan == null) return

      if (refAssetListTable.value && refAssetListTable.value.localItems) {
        for (let i = 0; i < refAssetListTable.value.localItems.length; i++) {
          const item = refAssetListTable.value.localItems[i]
          if (item && item.id === panic.assetId) {
            root.$set(item, 'pan', panic.pan)
          }
        }
      }
    }

    const realtimeUpdateAsset = (asset) => {
      if (!asset || !asset.id) return

      if (refAssetListTable.value && refAssetListTable.value.localItems) {
        for (let i = 0; i < refAssetListTable.value.localItems.length; i++) {
          const item = refAssetListTable.value.localItems[i]
          if (item && item.id === asset.id) {
            if (asset.lastWorkstatus !== undefined && asset.lastWorkstatus !== null) {
              root.$set(item, 'lastWorkstatus', asset.lastWorkstatus)
            }
            if (asset.lastPositionTime) {
              root.$set(item, 'lastPositionTime', asset.lastPositionTime)
              root.$set(item, 'LastPositionTime', asset.lastPositionTime)
              root.$set(item, 'lastConnectionTime', asset.lastPositionTime)
            }
            if (asset.lastTelemetryTime) {
              root.$set(item, 'lastTelemetryTime', asset.lastTelemetryTime)
              root.$set(item, 'LastTelemetryTime', asset.lastTelemetryTime)
            }
            if (asset.hourmeter !== undefined && asset.hourmeter !== null) {
              root.$set(item, 'hourmeter', asset.hourmeter)
              root.$set(item, 'Hourmeter', asset.hourmeter)
            }
            if (asset.odometer !== undefined && asset.odometer !== null) {
              root.$set(item, 'odometer', asset.odometer)
              root.$set(item, 'Odometer', asset.odometer)
            }
          }
        }
      }
    }

    const event_tlm = (content) => {
      if (!content || !content.assetId) return
      const data = content.data || {}

      const panic = {
        assetId: content.assetId,
        pan: data.pan,
      }

      const block = {
        assetId: content.assetId,
        ou1: data.ou1,
      }

      const time = content.deviceDateTime || content.serverDateTime

      const asset = {
        id: content.assetId,
        lastPositionTime: content.deviceDateTime || time,
        lastTelemetryTime: content.serverDateTime || time,
        lastWorkstatus: content.workStatus ?? content.workstatus,
        hourmeter: data.hor !== undefined ? data.hor : (data.hourmeter !== undefined ? data.hourmeter : undefined),
        odometer: data.hod !== undefined ? data.hod : (data.odometer !== undefined ? data.odometer : undefined),
      }

      realtimeUpdateBlock(block)
      realtimeUpdatePan(panic)
      realtimeUpdateAsset(asset)
    }

    const registerEventBusListeners = () => {
      root.$on("tlm", event_tlm)
    }

    const deregisterEventBusListeners = () => {
      root.$off("tlm", event_tlm)
    }

    const formatDate = (myDate) => {
      if (!myDate) return root.$t('Never Connected')
      const d = new Date(myDate)
      let dt = d.toLocaleDateString('pt-br')
      const hm = d.toLocaleTimeString().substring(0, 5)
      return `${dt} - ${hm}`
    }

    const hasMetricValue = (val) => {
      if (val === null || val === undefined || val === '') return false
      const n = Number(val)
      return Number.isFinite(n)
    }

    const formatMetricNumber = (val, decimals = 1) => {
      if (val === null || val === undefined || val === '') return '—'
      const n = Number(val)
      if (!Number.isFinite(n)) return '—'
      const locale = getIntlLocale ? getIntlLocale() : 'pt-BR'
      return n.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      })
    }

    const formatAssetDateTime = (myDate) => {
      if (!myDate) return null
      const d = new Date(myDate)
      if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1970) return null
      const locale = getIntlLocale ? getIntlLocale() : 'pt-BR'
      const dt = d.toLocaleDateString(locale)
      const hm = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
      return `${dt} - ${hm}`
    }

    const getPositionTime = (item) => {
      if (!item) return null
      if (item.lastPositionTime) return item.lastPositionTime
      if (item.LastPositionTime) return item.LastPositionTime
      return item.lastConnectionTime || null
    }

    const getTelemetryTime = (item) => {
      if (!item) return null
      if (item.lastTelemetryTime) return item.lastTelemetryTime
      if (item.LastTelemetryTime) return item.LastTelemetryTime
      return item.lastConnectionTime || null
    }

    const deleteAsset = (myId) => {
      store.dispatch('app-asset/deleteAsset', { id: myId })
      .then(response => {
        root.$swal(root.$t('Deleted'), root.$t('You successfully deleted'), 'success')
        .then((result) => {
          if (result.value) {
            refetchData()
          }
        })
      })
      .catch(error => {
        if (error.response && error.response.data) root.$swal(root.$t('Error'), root.$t('Unlink the device before removing the asset'), 'error')
        else root.$swal(root.$t('Error'), root.$t('An error occurred while trying to delete'), 'error')
      })
    }

    const modalDelete = (myId) => {
      root.$swal({
        title: root.$t('Are you sure?'),
        text: root.$t("You can't revert your action"),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: root.$t('Yes'),
        cancelButtonText: root.$t('No'),
        showCloseButton: true,
        showLoaderOnConfirm: true,
        customClass: {
          cancelButton: 'cancelBtn',
          confirmButton: 'mr-1',
        }
      }).then((result) => {
        if (result.value) {
          deleteAsset(myId)
        }
      })
    }

    const unBindAssetToAssetGroup = (assetId) => {
      store.dispatch('app-management/unBindAssetToAssetGroup', { assetId: assetId })
      .then(_ => {
        root.$swal(root.$t('Success'), root.$t('Asset removed from group'), 'success')
        .then(refetchData)
      })
      .catch(_ => {
        root.$swal(root.$t('Error'), root.$t('Error unbinding asset from group'), 'error')
      })
    }

    const showAssetGroupToggleModal = (assetId, assetGroup) => {
      if (assetGroup) {
        root.$swal({
          title: root.$t('Remove from group') + ` ${assetGroup}?`,
          text: root.$t("You can't revert your action"),
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: root.$t('Yes'),
          cancelButtonText: root.$t('No'),
          showCloseButton: true,
          showLoaderOnConfirm: true,
          customClass: {
            cancelButton: 'cancelBtn',
            confirmButton: 'mr-1',
          }
        }).then((result) => {
          if (result.value) {
            unBindAssetToAssetGroup(assetId)
          }
        })
        return
      }

      assetToAddToGroup.value = assetId
      root.$bvModal.show("asset-groups-modal")
    }

    const toNewMap = () => {
      router.push({ name: 'map' })
    }

    const onRowClicked = (item) => {
      if (!item || !item.id) return
      router.push({ name: 'apps-asset-summary', params: { id: item.id } })
    }

    // ====================================================
    // DRAG & DROP — Reordenação de colunas via cabeçalho da tabela
    // ====================================================
    const dragState = ref({
      draggingKey: null,
      dragOverKey: null,
    })

    /**
     * Encontra o index de uma coluna na ordem atual (columnOrder)
     * com base no atributo aria-colindex do <th>.
     */
    const getColumnKeyFromTh = (thElement) => {
      // b-table define aria-colindex (1-based) nos <th>
      const ariaIndex = parseInt(thElement.getAttribute('aria-colindex'), 10)
      if (!isNaN(ariaIndex) && tableColumns.value[ariaIndex - 1]) {
        return tableColumns.value[ariaIndex - 1].key
      }
      return null
    }

    const onHeaderDragStart = (event) => {
      const th = event.target.closest('th')
      if (!th) return
      const key = getColumnKeyFromTh(th)
      if (!key) return
      dragState.value.draggingKey = key
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', key)
      th.classList.add('dragging-column')
    }

    const onHeaderDragOver = (event) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      const th = event.target.closest('th')
      if (!th) return
      const key = getColumnKeyFromTh(th)
      if (key && key !== dragState.value.draggingKey) {
        dragState.value.dragOverKey = key
        th.classList.add('drag-over-column')
      }
    }

    const onHeaderDragLeave = (event) => {
      const th = event.target.closest('th')
      if (th) {
        th.classList.remove('drag-over-column')
      }
    }

    const onHeaderDrop = (event) => {
      event.preventDefault()
      const th = event.target.closest('th')
      if (!th) return
      th.classList.remove('drag-over-column')

      const fromKey = dragState.value.draggingKey
      const toKey = getColumnKeyFromTh(th)

      if (fromKey && toKey && fromKey !== toKey) {
        const order = [...columnOrder.value]
        const fromIdx = order.indexOf(fromKey)
        const toIdx = order.indexOf(toKey)
        if (fromIdx !== -1 && toIdx !== -1) {
          moveColumn(fromIdx, toIdx)
        }
      }

      dragState.value.draggingKey = null
      dragState.value.dragOverKey = null
    }

    const onHeaderDragEnd = (event) => {
      const th = event.target.closest('th')
      if (th) {
        th.classList.remove('dragging-column')
      }
      // Limpar todos os drag-over em caso de drop fora
      document.querySelectorAll('.drag-over-column').forEach(el => el.classList.remove('drag-over-column'))
      dragState.value.draggingKey = null
      dragState.value.dragOverKey = null
    }

    /**
     * Aplica os atributos draggable nos <th> do cabeçalho da tabela
     * após a montagem e sempre que as colunas mudam.
     */
    const setupDraggableHeaders = () => {
      nextTick(() => {
        if (!refAssetListTable.value || !refAssetListTable.value.$el) return
        const thead = refAssetListTable.value.$el.querySelector('thead')
        if (!thead) return

        const ths = thead.querySelectorAll('th')
        ths.forEach(th => {
          th.setAttribute('draggable', 'true')
          th.style.cursor = 'grab'
          th.removeEventListener('dragstart', onHeaderDragStart)
          th.removeEventListener('dragover', onHeaderDragOver)
          th.removeEventListener('dragleave', onHeaderDragLeave)
          th.removeEventListener('drop', onHeaderDrop)
          th.removeEventListener('dragend', onHeaderDragEnd)
          th.addEventListener('dragstart', onHeaderDragStart)
          th.addEventListener('dragover', onHeaderDragOver)
          th.addEventListener('dragleave', onHeaderDragLeave)
          th.addEventListener('drop', onHeaderDrop)
          th.addEventListener('dragend', onHeaderDragEnd)
        })
      })
    }

    // ====================================================
    // DRAG & DROP — Reordenação no dropdown do seletor de colunas
    // ====================================================
    const colSelectorDragKey = ref(null)

    const onColSelectorDragStart = (event, key) => {
      colSelectorDragKey.value = key
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', key)
      event.target.closest('.column-drag-item').classList.add('dragging-column')
    }

    const onColSelectorDragOver = (event) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      const item = event.target.closest('.column-drag-item')
      if (item) {
        item.classList.add('drag-over-column')
      }
    }

    const onColSelectorDragLeave = (event) => {
      const item = event.target.closest('.column-drag-item')
      if (item) {
        item.classList.remove('drag-over-column')
      }
    }

    const onColSelectorDrop = (event, targetKey) => {
      event.preventDefault()
      const item = event.target.closest('.column-drag-item')
      if (item) item.classList.remove('drag-over-column')

      const fromKey = colSelectorDragKey.value
      if (fromKey && targetKey && fromKey !== targetKey) {
        const order = [...columnOrder.value]
        const fromIdx = order.indexOf(fromKey)
        const toIdx = order.indexOf(targetKey)
        if (fromIdx !== -1 && toIdx !== -1) {
          moveColumn(fromIdx, toIdx)
        }
      }
      colSelectorDragKey.value = null
    }

    const onColSelectorDragEnd = (event) => {
      const item = event.target.closest('.column-drag-item')
      if (item) item.classList.remove('dragging-column')
      document.querySelectorAll('.drag-over-column').forEach(el => el.classList.remove('drag-over-column'))
      colSelectorDragKey.value = null
    }

    // ====================================================
    // ONBOARDING MODAL — Novas funcionalidades (PNL & UI/UX)
    // ====================================================
    const ASSET_LIST_NEW_FEATURES_KEY = 'infofleet_asset_list_features_intro_v1'
    const showNewFeaturesModal = ref(false)
    const dontShowAgainNewFeatures = ref(true)

    const openNewFeaturesModal = () => {
      showNewFeaturesModal.value = true
    }

    const closeNewFeaturesModal = () => {
      showNewFeaturesModal.value = false
      if (dontShowAgainNewFeatures.value) {
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(ASSET_LIST_NEW_FEATURES_KEY, 'true')
          }
        } catch (e) {
          // localStorage error safety
        }
      }
    }

    watch(tableColumns, () => {
      setupDraggableHeaders()
    }, { deep: true, immediate: true })

    onMounted(() => {
      setupDraggableHeaders()
      registerEventBusListeners()
      fetchTypesWorkStatus()

      store.dispatch('app-asset/fetchAssetsManufacture')
      .then(response => {
        response.data.data.forEach(v => {
          manufacturerOptions.value.push({
            label: v.name,
            value: v.id
          })
        })
      })
      .catch(() => {})

      store.dispatch('app-asset/fetchAssetsType')
      .then(response => {
        response.data.data.forEach(v => {
          assetsOptions.value.push({
            label: v.name,
            value: v.id
          })
        })
      })
      .catch(() => {})

      // Exibe modal apenas na primeira vez que o usuario entra na tela
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const hasSeen = localStorage.getItem(ASSET_LIST_NEW_FEATURES_KEY)
          if (!hasSeen) {
            setTimeout(() => {
              showNewFeaturesModal.value = true
            }, 700)
          }
        }
      } catch (e) {
        // localStorage error safety
      }
    })

    onBeforeUnmount(() => {
      deregisterEventBusListeners()
    })

    return {
      // Returned from useAssetList
      fetchAssets,
      tableColumns,
      allColumns,
      availableColumns,
      orderedAvailableColumns,
      selectedColumns,
      columnOrder,
      selectAllColumns,
      deselectAllColumns,
      resetColumnsToDefault,
      moveColumn,
      moveColumnByKey,
      perPage,
      currentPage,
      totalAssets,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      sortDesc,
      refAssetListTable,
      statusFilter,
      customerId,
      chosenStatus,
      showFilters,
      searchOwner,
      assetBrandId,
      assetTypeId,
      assetGroupId,
      refetchData,
      resolveModelAvatarVariant,
      resolveAssetStatusVariantAndIcon,
      resolveIcon,
      clearFilters,
      updateSearchOwner,
      updateCustomerId,
      updateAssetGroupId,

      // Component Specific data
      assetToAddToGroup,
      emptyText,
      titleTootipBtnFilter,
      titleTootipBtnCloseFilter,
      titleFuellingBtn,
      cloningAssetId,
      datasuggest,
      filteredOptions,
      inputProps,
      limit,
      selected,
      options,
      searchAssetGroup,
      filteredOptionsAssetGroup,
      inputPropsAssetGroup,
      changeLogAssetId,
      changeLogSearchUser,
      changeLogSelectedUserId,
      changeLogFilteredUsers,
      changeLogInputPropsUser,
      changeLogItems,
      changeLogFields,
      changeLogDateRange,
      changeLogDateConfig,
      isChangeLogBusy,
      changeLogCurrentPage,
      changeLogTotalRows,
      changeLogMeta,
      manufacturerOptions,
      assetsOptions,
      statusOptions,
      user,
      hasActiveFilters,
      placeholderSelect,
      skeletonRows,

      // Methods
      showChangeLogModal,
      clearChangeLogFilter,
      fetchChangeLogData,
      changeLogSelectUserHandler,
      changeLogOnInputUserChange,
      cloneAsset,
      selectHandlerAssetGroup,
      onInputChangeAssetGroup,
      onAssetAddedToGroup,
      showModalExport,
      hideModalExport,
      exportXls,
      exportPDF,
      resolveTextStatus,
      fetchTypesWorkStatus,
      selectHandler,
      onDropdownShow,
      onInputChange,
      clearAllFilters,
      resolveClass,
      setColor,
      resolveColorNoConnection,
      realtimeUpdateBlock,
      realtimeUpdatePan,
      realtimeUpdateAsset,
      event_tlm,
      formatDate,
      hasMetricValue,
      formatMetricNumber,
      formatAssetDateTime,
      getPositionTime,
      getTelemetryTime,
      deleteAsset,
      modalDelete,
      unBindAssetToAssetGroup,
      showAssetGroupToggleModal,
      toNewMap,
      avatarText,
      onRowClicked,
      
      // Drag and Drop
      dragState,
      onHeaderDragStart,
      onHeaderDragOver,
      onHeaderDragLeave,
      onHeaderDrop,
      onHeaderDragEnd,
      colSelectorDragKey,
      onColSelectorDragStart,
      onColSelectorDragOver,
      onColSelectorDragLeave,
      onColSelectorDrop,
      onColSelectorDragEnd,

      // Onboarding Modal
      showNewFeaturesModal,
      dontShowAgainNewFeatures,
      openNewFeaturesModal,
      closeNewFeaturesModal,
    }
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss" scoped>

// ====================================================
// FILTER PANEL
// ====================================================
.filter-panel {
  border: none;
  border-top: 3px solid #7367f0;
  border-radius: 0.6rem;
  box-shadow: 0 4px 24px rgba(115, 103, 240, 0.10);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1rem;
    background: transparent;
    border-bottom: 1px solid rgba(0,0,0,0.06);
  }

  &__icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  &__body {
    padding: 1rem;
  }
}

.filter-active-badge {
  font-size: 0.65rem;
  padding: 0.25em 0.6em;
  letter-spacing: 0.03em;
}

.filter-label {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.filter-close-btn {
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

// ====================================================
// SLIDE-DOWN TRANSITION
// ====================================================
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// ====================================================
// DRAG & DROP
// ====================================================
.cursor-move {
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
}

.dragging-column {
  opacity: 0.5;
  background: rgba(115, 103, 240, 0.05);
}

.drag-over-column {
  border-left: 2px solid #7367f0 !important;
}

::v-deep th[draggable="true"] {
  position: relative;
  transition: background 0.15s ease;
  &:hover {
    background: rgba(115, 103, 240, 0.04);
  }
}

// ====================================================
// TOOLBAR
// ====================================================
.asset-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.toolbar-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1;
  min-width: 0;
}

.per-page-control {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.per-page-label {
  font-size: 0.85rem;
  color: #6e6b7b;
  white-space: nowrap;
  margin: 0;
}

.per-page-selector {
  width: 84px;
}

// Search Input — expande para ocupar todo o espaço disponível
.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  .search-icon {
    position: absolute;
    left: 10px;
    color: #b9b9c3;
    pointer-events: none;
    z-index: 2;
  }

  .search-input {
    padding-left: 34px;
    width: 100%;
    border-radius: 8px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
    }
  }
}

// Separador vertical entre search e action buttons
.toolbar-separator {
  width: 1px;
  height: 24px;
  background: rgba(0,0,0,0.10);
  flex-shrink: 0;
}

// Grupo de botões de ação — não comprime, gap consistente
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.toolbar-btn {
  border-radius: 8px !important;
  transition: all 0.15s ease !important;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
  }
}

.clear-filters-btn {
  white-space: nowrap;
}

// ====================================================
// BADGE NOTIFICATION (filter button)
// ====================================================
.badge-notification {
  top: -5px;
  right: -5px;
  font-size: 0.6rem;
  min-width: 16px;
  height: 16px;
  line-height: 14px;
}

// ====================================================
// COLUMN DROPDOWN
// ====================================================
.column-dropdown-content {
  min-width: 240px;
  max-width: 300px;
}

.custom-column-checkbox {
  ::v-deep .custom-control-label {
    cursor: pointer;
    user-select: none;
    line-height: 1.4;
  }
}

// ====================================================
// DATA TABLE
// ====================================================
.asset-data-table {
  ::v-deep tbody tr.asset-table-row {
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
    cursor: pointer;

    &:hover {
      background-color: rgba(115, 103, 240, 0.04) !important;
      box-shadow: inset 3px 0 0 #7367f0;
    }
  }

  ::v-deep tr.b-table-busy-slot {
    > td {
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
    }
  }

  // Sincronização de larguras entre os cabeçalhos <th> e as células do skeleton
  ::v-deep {
    th.col-ownername, td.col-ownername { min-width: 170px; width: 180px; }
    th.col-costCenter, td.col-costCenter { min-width: 110px; width: 120px; }
    th.col-plate, td.col-plate { min-width: 110px; width: 120px; }
    th.col-nickname, td.col-nickname { min-width: 130px; width: 140px; }
    th.col-modelName, td.col-modelName { min-width: 200px; width: 220px; }
    th.col-chassis, td.col-chassis { min-width: 150px; width: 160px; }
    th.col-hourmeter, td.col-hourmeter { min-width: 105px; width: 115px; }
    th.col-odometer, td.col-odometer { min-width: 105px; width: 115px; }
    th.col-lastPositionTime, td.col-lastPositionTime { min-width: 135px; width: 145px; }
    th.col-lastTelemetryTime, td.col-lastTelemetryTime { min-width: 135px; width: 145px; }
    th.col-workstatus, td.col-workstatus { min-width: 125px; width: 135px; }
    th.col-actions, td.col-actions { min-width: 65px; width: 70px; text-align: center; }
  }
}

// ====================================================
// SKELETON TABLE ENHANCEMENTS & MODERN ANIMATIONS
// ====================================================
.asset-table-skeleton-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;

  .asset-skeleton-table {
    width: 100%;
    margin-bottom: 0;
    border-collapse: collapse;

    tr.asset-skeleton-row {
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(115, 103, 240, 0.02);
      }

      td.asset-skeleton-cell {
        vertical-align: middle;
        padding: 0.72rem 1rem;
        border-top: none;
      }
    }
  }
}

// Plate Chip Skeleton
.plate-chip--skeleton {
  pointer-events: none;
  background: rgba(115, 103, 240, 0.05) !important;
  border: 1px dashed rgba(115, 103, 240, 0.25) !important;
  user-select: none;
  cursor: default;

  .plate-sk-icon {
    opacity: 0.7;
  }
}

// Status Badge Skeleton
.status-badge--skeleton {
  pointer-events: none;
  background: rgba(115, 103, 240, 0.06);
  border: 1px solid rgba(115, 103, 240, 0.12);
  user-select: none;
  cursor: default;

  .skeleton-led {
    animation: skeleton-led-pulse 1.6s ease-in-out infinite;

    &--success   { background: #28c76f; box-shadow: 0 0 6px rgba(40, 199, 111, 0.6); }
    &--warning   { background: #ff9f43; box-shadow: 0 0 6px rgba(255, 159, 67, 0.6); }
    &--danger    { background: #ea5455; box-shadow: 0 0 6px rgba(234, 84, 85, 0.6); }
    &--info      { background: #00cfe8; box-shadow: 0 0 6px rgba(0, 207, 232, 0.6); }
    &--secondary { background: #82868b; box-shadow: 0 0 6px rgba(130, 134, 139, 0.6); }
  }
}

@keyframes skeleton-led-pulse {
  0% {
    transform: scale(0.9);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.25);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.5;
  }
}

.action-sk-btn {
  opacity: 0.5;
}

.model-sk-avatar {
  opacity: 0.85;
}

// Shimmer wave refinement for Bootstrap BSkeleton
::v-deep .b-skeleton {
  background-color: rgba(234, 236, 241, 0.7) !important;
  border-radius: 4px;

  &.b-skeleton-animate-wave {
    background: linear-gradient(
      90deg,
      rgba(234, 236, 241, 0.7) 0%,
      rgba(250, 251, 253, 0.95) 50%,
      rgba(234, 236, 241, 0.7) 100%
    ) !important;
    background-size: 200% 100% !important;
    animation-delay: var(--sk-delay, 0s) !important;
    animation-duration: 1.4s !important;
  }
}

// Owner cell

// Plate chip
.plate-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.2em 0.65em;
  background: rgba(115, 103, 240, 0.08);
  border: 1px solid rgba(115, 103, 240, 0.18);
  border-radius: 5px;
  color: #5e5873;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  transition: all 0.15s ease;
  text-decoration: none;

  &:hover {
    background: rgba(115, 103, 240, 0.15);
    border-color: #7367f0;
    color: #7367f0;
  }
}

.nickname-link {
  transition: color 0.12s ease;

  &:hover {
    color: darken(#7367f0, 10%) !important;
  }
}

// Last connection cell (legacy)
.last-connection-cell {
  display: inline-flex;
  align-items: center;
  font-size: 0.84rem;
  color: #6e6b7b;
}

// Dual connection cell (Position & Telemetry)
.cell-connection-dual {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 175px;
  padding: 2px 0;

  .connection-row {
    display: inline-flex;
    align-items: center;
    line-height: 1.25;
    cursor: default;
  }

  .connection-pill {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    margin-right: 6px;
    flex-shrink: 0;
    line-height: 1.2;

    .pill-icon {
      flex-shrink: 0;
    }

    &--gps {
      background-color: rgba(0, 114, 255, 0.09);
      color: #0070e0;
      border: 1px solid rgba(0, 114, 255, 0.18);
    }

    &--tlm {
      background-color: rgba(115, 103, 240, 0.09);
      color: #7367f0;
      border: 1px solid rgba(115, 103, 240, 0.18);
    }
  }

  .connection-date {
    font-size: 0.81rem;
    font-weight: 500;
    color: #5e5873;
    white-space: nowrap;
    letter-spacing: -0.01em;
  }
}

.connection-empty {
  display: inline-flex;
  align-items: center;
  color: #b9b9c3;
  font-size: 0.82rem;
}

// Status badges with LED dot
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0.22em 0.7em;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;

  .status-led {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: currentColor;
  }

  &--success  { background: rgba(40,199,111,0.12);  color: #28c76f; }
  &--secondary { background: rgba(130,134,139,0.12); color: #82868b; }
  &--warning  { background: rgba(255,159,67,0.12);  color: #ff9f43; }
  &--info     { background: rgba(0,207,232,0.12);   color: #00cfe8; }
  &--light    { background: rgba(186,191,199,0.12); color: #b9b9c3; }
  &--danger   { background: rgba(234,84,85,0.12);   color: #ea5455; }

  &--pulse .status-led {
    animation: led-pulse 1.2s ease-in-out infinite;
  }
}

@keyframes led-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.6); }
  70%  { box-shadow: 0 0 0 6px rgba(234, 84, 85, 0); }
  100% { box-shadow: 0 0 0 0 rgba(234, 84, 85, 0); }
}

// Model / Chassis cell
.model-cell {
  display: flex;
  align-items: center;

  .model-avatar {
    border-radius: 10px;
    flex-shrink: 0;
  }
}

.chassis-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  color: #6e6b7b;
  text-decoration: none;
  transition: color 0.12s ease;

  &:hover {
    color: #7367f0;
  }
}

// Actions cell
.actions-cell {
  white-space: nowrap;
}

.action-menu-btn {
  padding: 4px 6px !important;
  border-radius: 6px !important;
  color: #b9b9c3;
  transition: background 0.12s ease, color 0.12s ease !important;

  &:hover {
    background: rgba(115, 103, 240, 0.08) !important;
    color: #7367f0 !important;
  }
}

// ====================================================
// FOOTER / PAGINATION
// ====================================================
.asset-list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-top: 1px solid rgba(0,0,0,0.05);
}

.footer-meta {
  display: flex;
  align-items: center;
  font-size: 0.84rem;
}

.pagination-modern {
  ::v-deep .page-item {
    &.active .page-link {
      border-radius: 7px;
      background: #7367f0;
      border-color: #7367f0;
      box-shadow: 0 3px 10px rgba(115, 103, 240, 0.4);
    }

    .page-link {
      border-radius: 7px;
      font-weight: 500;
      transition: all 0.15s ease;

      &:hover {
        background: rgba(115, 103, 240, 0.08);
        color: #7367f0;
      }
    }
  }
}

// ====================================================
// EXPORT MODAL
// ====================================================
.export-modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem 1rem;
}

.export-spinner-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
}

// ====================================================
// FIELD CHIP (Change Log modified fields)
// ====================================================
.field-chip {
  display: inline-flex;
  padding: 0.18em 0.6em;
  background: rgba(115, 103, 240, 0.08);
  border: 1px solid rgba(115, 103, 240, 0.15);
  border-radius: 4px;
  font-size: 0.75rem;
  color: #7367f0;
  font-weight: 500;
}

// ====================================================
// MISC: Maintain card overflow visible for dropdowns
// ====================================================
.asset-list-card {
  overflow: visible;

  ::v-deep .card-body {
    overflow: visible;
  }
}

.asset-list-top-actions {
  position: relative;
  z-index: 10;
}

// ====================================================
// RESPONSIVE MOBILE & SCROLL FIX
// ====================================================
::v-deep .table-responsive {
  margin-bottom: 0;
  
  // Evita scroll horizontal desnecessário causado por margin-bottom da tabela
  .table {
    margin-bottom: 0;
  }
}

@media (max-width: 767.98px) {
  .asset-list-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  .toolbar-left, .toolbar-right {
    width: 100%;
    justify-content: flex-start;
  }
  .search-input-wrapper {
    width: 100%;
    flex: 1;
    .search-input {
      max-width: 100%;
      min-width: 100%;
    }
  }
  .asset-list-footer {
    justify-content: center;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

// Menus do topo não ficam atrás de headers/footers
.asset-list-top-options-menu,
.asset-list-column-menu {
  z-index: 99999 !important;
}

.asset-list-column-menu {
  min-width: 260px;
  max-width: 320px;
  box-shadow: 0 8px 30px 0 rgba(34, 41, 47, 0.18);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 0.6rem;
}

// ====================================================
// NEW FEATURES WELCOME MODAL (PNL & Modern UI/UX)
// ====================================================
.new-features-modal {
  .modal-dialog {
    max-width: 800px;
  }

  .modal-content {
    border-radius: 18px;
    border: none;
    box-shadow: 0 15px 40px rgba(34, 41, 47, 0.15);
    overflow: hidden;
  }
}

.new-features-container {
  background: #ffffff;
  position: relative;

  .new-features-close-btn {
    position: absolute;
    top: 14px;
    right: 18px;
    background: rgba(115, 103, 240, 0.08);
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    color: #6e6b7b;
    transition: all 0.2s ease;
    z-index: 2;

    &:hover {
      background: rgba(115, 103, 240, 0.18);
      color: #7367f0;
      transform: scale(1.05);
    }
  }

  .new-features-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.9rem;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.12), rgba(0, 114, 255, 0.1));
    color: #7367f0;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .new-features-title {
    color: #3b3749;
    font-size: 1.45rem;
    line-height: 1.3;
  }

  .new-features-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    @media (max-width: 767.98px) {
      grid-template-columns: 1fr;
    }
  }

  .feature-card {
    background: #f8f8fb;
    border: 1px solid rgba(115, 103, 240, 0.08);
    border-radius: 14px;
    padding: 1.1rem;
    display: flex;
    flex-direction: column;
    transition: all 0.25s ease;

    &:hover {
      background: #ffffff;
      border-color: rgba(115, 103, 240, 0.25);
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(115, 103, 240, 0.08);
    }

    .feature-icon-wrapper {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;

      &--primary {
        background: rgba(115, 103, 240, 0.12);
        color: #7367f0;
      }
      &--info {
        background: rgba(0, 114, 255, 0.12);
        color: #0070e0;
      }
      &--success {
        background: rgba(40, 199, 111, 0.12);
        color: #28c76f;
      }
    }

    .feature-card-title {
      font-size: 0.95rem;
      color: #383446;
      line-height: 1.3;
    }

    .feature-card-text {
      line-height: 1.45;
      flex-grow: 1;
    }

    .feature-preview-chips {
      margin-top: auto;
      padding-top: 6px;
    }
  }

  .preview-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.22rem 0.55rem;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 600;

    &--metric {
      background: #ffffff;
      border: 1px solid #e2e1ec;
      color: #5e5873;
    }

    &--action {
      background: rgba(115, 103, 240, 0.09);
      border: 1px solid rgba(115, 103, 240, 0.2);
      color: #7367f0;
    }
  }

  .pro-tip-banner {
    background: linear-gradient(135deg, rgba(255, 159, 67, 0.09), rgba(115, 103, 240, 0.05));
    border: 1px solid rgba(255, 159, 67, 0.25);
    border-radius: 12px;
    padding: 0.75rem 1rem;

    .pro-tip-icon {
      flex-shrink: 0;
    }

    .pro-tip-text {
      line-height: 1.4;
      color: #5e5873;
    }
  }

  .btn-cta-welcome {
    border-radius: 10px;
    padding: 0.6rem 1.4rem;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.25);
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(115, 103, 240, 0.35);
    }
  }
}

// Dark Mode Support
.dark-layout {
  .new-features-container {
    background: #283046;

    .new-features-close-btn {
      background: rgba(255, 255, 255, 0.06);
      color: #b4b7bd;

      &:hover {
        background: rgba(115, 103, 240, 0.25);
        color: #fff;
      }
    }

    .new-features-title {
      color: #d0d2d6;
    }

    .feature-card {
      background: #161d31;
      border-color: rgba(255, 255, 255, 0.07);

      &:hover {
        background: #1f2740;
        border-color: rgba(115, 103, 240, 0.35);
      }

      .feature-card-title {
        color: #d0d2d6;
      }
    }

    .preview-chip--metric {
      background: #283046;
      border-color: rgba(255, 255, 255, 0.1);
      color: #d0d2d6;
    }

    .pro-tip-banner {
      background: rgba(255, 159, 67, 0.08);
      border-color: rgba(255, 159, 67, 0.2);

      .pro-tip-text {
        color: #d0d2d6;
      }
    }
  }

  .asset-data-table {
    ::v-deep tbody tr.asset-table-row:hover {
      background-color: rgba(115, 103, 240, 0.1) !important;
      box-shadow: inset 3px 0 0 #7367f0;
    }
  }

  .asset-table-skeleton-wrapper {
    .asset-skeleton-table {
      tr.asset-skeleton-row {
        border-bottom-color: rgba(255, 255, 255, 0.07);

        &:hover {
          background-color: rgba(115, 103, 240, 0.06);
        }
      }
    }
  }

  .plate-chip--skeleton {
    background: rgba(115, 103, 240, 0.12) !important;
    border-color: rgba(115, 103, 240, 0.35) !important;
  }

  .status-badge--skeleton {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
  }

  ::v-deep .b-skeleton {
    background-color: rgba(255, 255, 255, 0.08) !important;

    &.b-skeleton-animate-wave {
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.07) 0%,
        rgba(255, 255, 255, 0.16) 50%,
        rgba(255, 255, 255, 0.07) 100%
      ) !important;
    }
  }
}
</style>
